// Leave-tab helpers — the annual quota model + taken counts for การลา.
// Backend: employee_timetable rows of type ลากิจ/ลาป่วย/Leave without pay, via
// /api/hr/leave. Leave without pay is counted (`unpaid`) but is NEVER part of a
// quota — that is the whole point of the type (Ram, 2026-10-31).
// Pure (no I/O). Quota enums/labels/month math live in ./timetable.js.
//
// QUOTA (business rule, per the user — independent of SSO):
//   personal (ลากิจ) = 12 days/year, EXCEPT in the calendar year probation is
//     passed, where the months BEFORE `date_probationpass` are deducted:
//       · deduct = (probation_month − 1)                 if probation day == 1
//       · deduct = (probation_month − 1) + 1  (skip that month too)  if day > 1
//     e.g. (selected year 2026): probationpass 1/5/2026 → 12−4 = 8;
//          10/5/2026 → 12−5 = 7;  1/5/2025 (prior year) → 12.
//   sick (ลาป่วย)   = 30 days/year, fixed (never prorated).
import { parseDate } from './employee.js'
import { LEAVE_PERSONAL, LEAVE_SICK, LEAVE_UNPAID, MONTHS_TH_SHORT } from './timetable.js'

export const PERSONAL_QUOTA = 12
export const SICK_QUOTA = 30

// Full annual ลากิจ entitlement for `emp` in calendar `year` (before subtracting
// taken). Full 12 for a year fully worked; in the probation-pass year the months
// before `date_probationpass` are deducted (the probation month itself only
// counts toward entitlement when probation was passed on the 1st).
export function personalEntitlement(emp, year) {
  const d = parseDate(emp?.date_probationpass)
  const Y = Number(year)
  if (!d) return PERSONAL_QUOTA            // no probation date on record → assume full year
  const py = d.getFullYear()
  if (py > Y) return 0                      // probation not yet passed in this year → no entitlement
  if (py < Y) return PERSONAL_QUOTA         // a full year of entitlement
  // probation passed within the selected year → deduct the months before it.
  const monthsBefore = d.getMonth()         // getMonth() is 0-based = count of Jan..(prev month)
  const deduct = d.getDate() === 1 ? monthsBefore : monthsBefore + 1
  return Math.max(0, PERSONAL_QUOTA - deduct)
}

// Full annual ลาป่วย entitlement — fixed 30/year for everyone.
export function sickEntitlement() {
  return SICK_QUOTA
}

// { personal, sick, unpaid, total, rows } per employee_id, from a flat list of leave rows.
export function countLeaveByEmployee(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const id = Number(r?.employee_id)
    if (!Number.isFinite(id)) continue
    let g = m.get(id)
    if (!g) { g = { personal: 0, sick: 0, unpaid: 0, total: 0, rows: [] }; m.set(id, g) }
    if (r.type === LEAVE_SICK) g.sick += 1
    else if (r.type === LEAVE_PERSONAL) g.personal += 1
    else if (r.type === LEAVE_UNPAID) g.unpaid += 1
    g.total += 1
    g.rows.push(r)
  }
  return m
}

// The quota view-model for one employee: used / quota / remaining / over-quota,
// for both leave types, given the already-counted `taken` = { personal, sick }.
export function leaveQuota(emp, year, taken) {
  const sso = !!emp?.sso
  const usedP = Number(taken?.personal) || 0
  const usedS = Number(taken?.sick) || 0
  const quotaP = personalEntitlement(emp, year)
  const quotaS = sickEntitlement()
  return {
    sso,
    personal: { used: usedP, quota: quotaP, remaining: quotaP - usedP, over: usedP > quotaP },
    sick: { used: usedS, quota: quotaS, remaining: quotaS - usedS, over: usedS > quotaS },
  }
}

// Blank leave-day for the editor. `defaults` seeds employee/branch/date/type.
export function blankLeave(defaults = {}) {
  return {
    id: null,
    employee_id: '',
    branch_id: '',
    type: LEAVE_PERSONAL,
    date: '',
    unnotice: false,
    med_certificate: false,
    remark: '',
    ...defaults,
  }
}

// Jan 1 → Dec 31 ISO range for the annual quota fetch.
export function yearRange(year) {
  return { start_date: `${year}-01-01`, end_date: `${year}-12-31` }
}

// dd/mm/yyyy (Gregorian / ค.ศ. year) — the Leave tab's date format.
export function fmtDMY(v) {
  const d = parseDate(v)
  if (!d) return '—'
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
}

// ── รอบ (payroll cycle): the 26th of the previous month → the 25th of this ──
// A leave date belongs to the รอบ that ENDS on the 25th of its month, unless it
// falls on/after the 26th, which rolls it into the next month's รอบ.
export function payrollRoundOf(dateStr) {
  const d = parseDate(dateStr)
  if (!d) return null
  const p = (n) => String(n).padStart(2, '0')
  let ey = d.getFullYear()
  let em = d.getMonth() + 1                // 1-12, the round's END month
  if (d.getDate() >= 26) { em += 1; if (em > 12) { em = 1; ey += 1 } }
  let sy = ey, sm = em - 1                 // start = 26th of the previous month
  if (sm < 1) { sm = 12; sy -= 1 }
  return { key: `${ey}-${p(em)}`, ey, em, sy, sm, start: `${sy}-${p(sm)}-26`, end: `${ey}-${p(em)}-25` }
}

// "รอบ 26 ก.ค. – 25 ส.ค. 2026" — Gregorian (ค.ศ.) label for a รอบ.
export function fmtRoundLabel(r) {
  if (!r) return '—'
  return `รอบ 26 ${MONTHS_TH_SHORT[r.sm - 1]} – 25 ${MONTHS_TH_SHORT[r.em - 1]} ${r.ey}`
}

// Bucket leave rows into รอบ groups, newest รอบ first.
export function groupRowsByRound(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const round = payrollRoundOf(r?.date)
    if (!round) continue
    let g = m.get(round.key)
    if (!g) { g = { round, rows: [] }; m.set(round.key, g) }
    g.rows.push(r)
  }
  return [...m.values()]
    .sort((a, b) => b.round.key.localeCompare(a.round.key))
    .map((g) => ({ ...g, rows: g.rows.sort((a, b) => String(b.date).localeCompare(String(a.date))) }))
}

// Coerce a /leave/summary row (Postgres returns numerics as strings).
export function normLeaveSummary(r) {
  const n = (v) => Number(v) || 0
  return {
    key: String(r?.key ?? r?.employee_id ?? ''),
    employee_id: n(r?.employee_id),
    code: r?.code || '',
    name: r?.name || '',
    nickname: r?.nickname || '',
    branch_id: n(r?.branch_id),
    sso: r?.sso === true || r?.sso === 'true' || r?.sso === 1 || r?.sso === '1',
    total: n(r?.total),
    personal: n(r?.personal),
    sick: n(r?.sick),
    unpaid: n(r?.unpaid),
    unnotice: n(r?.unnotice),
    med_certificate: n(r?.med_certificate),
  }
}

// Totals strip across all summary rows (the conclusion header).
export function totalLeaveSummary(rows) {
  const acc = { people: 0, total: 0, personal: 0, sick: 0, unpaid: 0, unnotice: 0, med_certificate: 0 }
  for (const r of Array.isArray(rows) ? rows : []) {
    acc.people += 1
    acc.total += r.total; acc.personal += r.personal; acc.sick += r.sick; acc.unpaid += r.unpaid
    acc.unnotice += r.unnotice; acc.med_certificate += r.med_certificate
  }
  return acc
}

// Pull a human message out of a thrown api error ("HTTP 400: {\"message\":…}").
// Xano's create precondition (a shift already exists that day) lands here.
export function leaveError(e) {
  const raw = String(e?.message || e || '')
  const brace = raw.indexOf('{')
  if (brace >= 0) {
    try {
      const o = JSON.parse(raw.slice(brace))
      if (o && (o.message || o.error)) return o.message || o.error
    } catch { /* not json — fall through */ }
  }
  return raw || 'เกิดข้อผิดพลาด'
}
